import { Users, TrendingUp, Lightbulb, Target, Shield, Handshake } from 'lucide-react';

const iconMap = {
  Users,
  TrendingUp,
  Lightbulb,
  Target,
  Shield,
  Handshake
};

const defaultMissions = [
  { icon: 'Users', title: 'Fédérer les acteurs', description: 'Réunir les représentants de la communauté financière et économique de notre région' },
  { icon: 'TrendingUp', title: 'Favoriser le développement', description: 'Favoriser le développement des entreprises de Bourgogne-Franche-Comté' },
  { icon: 'Lightbulb', title: 'Innover ensemble', description: 'Déployer les outils innovants dans le monde des affaires' },
  { icon: 'Target', title: 'Créer de la valeur', description: 'Accompagner la création de valeur au sein des entreprises du territoire' },
  { icon: 'Shield', title: 'Éthique et proximité', description: 'Garantir la neutralité et un ancrage territorial fort' }
];

const MissionsSection = ({ content, images }) => {
  const missions = content?.items || defaultMissions;
  const extra = content?.extra_fields || {};

  return (
    <section
      id="missions"
      data-testid="missions-section"
      className="py-24 lg:py-32 bg-slate-50"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1.5 bg-[#1e3a8a]/10 text-[#1e3a8a] text-sm font-inter font-medium tracking-wide uppercase rounded-full mb-4">
            {extra.badge_text || "Notre raison d'être"}
          </span>
          <h2 className="font-playfair text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4">
            {content?.title || 'Nos missions'}
          </h2>
          <div className="w-20 h-1 bg-gradient-to-r from-[#1e3a8a] to-[#D4AF37] mx-auto mb-6" />
          <p className="text-lg text-slate-600 font-inter max-w-3xl mx-auto">
            {content?.subtitle || 'Créée fin 2017, la Place Financière BFC est une instance de concertation'}
          </p>
        </div>

        {/* Content text */}
        {content?.content && (
          <div className="max-w-3xl mx-auto text-center mb-16">
            <p className="text-slate-600 font-inter leading-relaxed">
              {content.content}
            </p>
          </div>
        )}

        {/* Missions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {missions.map((mission, idx) => {
            const IconComponent = iconMap[mission.icon] || Users;
            return (
              <div
                key={idx}
                className="bg-white p-8 shadow-sm hover:shadow-xl transition-all duration-500 group relative overflow-hidden gold-border-animate card-lift"
                data-testid={`mission-card-${idx}`}
              >
                {/* Icon */}
                <div className="w-16 h-16 bg-slate-50 group-hover:bg-[#1e3a8a]/5 rounded-lg flex items-center justify-center mb-6 transition-colors duration-300">
                  <IconComponent className="w-8 h-8 text-[#D4AF37]" strokeWidth={1.5} />
                </div>
                
                {/* Content */}
                <h3 className="font-playfair text-xl font-semibold text-[#0F172A] mb-3">
                  {mission.title}
                </h3>
                <p className="text-slate-600 font-inter text-sm leading-relaxed">
                  {mission.description}
                </p>

                {/* Number badge */}
                <div className="absolute top-4 right-4 w-8 h-8 bg-slate-50 group-hover:bg-[#D4AF37] rounded-full flex items-center justify-center transition-colors duration-300">
                  <span className="text-sm font-inter font-medium text-slate-400 group-hover:text-white transition-colors duration-300">
                    {String(idx + 1).padStart(2, '0')}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Values section */}
        <div className="mt-20 bg-[#1e3a8a] text-white p-8 lg:p-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="font-playfair text-2xl lg:text-3xl font-semibold mb-4">
                {extra.values_title || 'Nos valeurs'}
              </h3>
              <p className="font-inter text-white/80 leading-relaxed">
                {extra.values_content || "L'éthique des affaires, la neutralité d'une place financière, un cadre apolitique, le respect entre les personnes, la proximité et l'ancrage territorial, l'esprit d'innovation et de créativité."}
              </p>
            </div>
            <div className="lg:text-right">
              <p className="text-lg font-inter italic text-white/90">
                "{extra.values_quote || "La Place Financière est ouverte à tous les dirigeants d'entreprises, et aux professionnels du droit, du chiffre et de la finance."}"
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MissionsSection;
